import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { selectClass } from '../../../components/ui/FormField'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useMapaResultados, useCreateResultado, useUpdateResultado, useReprocessarResultados } from '../hooks/useResultados'
import { api } from '../../../api/axios'
import { useNetworkStatus } from '../../../hooks/useOfflineSync'
import { useToast } from '../../../components/offline/ToastProvider'
import { useAuth } from '../../../contexts/AuthContext'
import { printComprovanteResultadoThermal } from '../../../lib/printThermal'
import { bluetoothPrinter } from '../../../lib/bluetoothPrinter'
import type { PrintResultadoOpts } from '../../../lib/bluetoothPrinter'
import type { MapaResultadoItem } from '../types'
import type { Etapa } from '../../etapas/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso.includes('T') ? iso : iso + 'T12:00:00').toLocaleDateString('pt-BR')
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR')
}

// ── Formulário Precisão ────────────────────────────────────────────────────

interface FormPrecisaoState {
  qtd_x: string; qtd_10: string; qtd_9: string; qtd_8: string; qtd_7: string
  qtd_6: string; qtd_5: string; qtd_4: string; qtd_3: string; qtd_2: string
  qtd_1: string; qtd_0: string; qtd_penalidade: string
}

const PRECISAO_DEFAULTS: FormPrecisaoState = {
  qtd_x: '0', qtd_10: '0', qtd_9: '0', qtd_8: '0', qtd_7: '0',
  qtd_6: '0', qtd_5: '0', qtd_4: '0', qtd_3: '0', qtd_2: '0',
  qtd_1: '0', qtd_0: '0', qtd_penalidade: '0',
}

// Penalidade so se aplica ao Tiro de Precisao Duelo 20 Seg (Pistola/Revolver) —
// cada penalidade desconta 10 pontos do total (mesma regra do backend).
const PONTOS_POR_PENALIDADE = 10

function modalidadeTemPenalidade(nomeModalidade: string | undefined): boolean {
  return (nomeModalidade ?? '').toUpperCase().includes('DUELO')
}

const PRECISAO_FIELDS: Array<{ key: keyof FormPrecisaoState; label: string; peso: number }> = [
  { key: 'qtd_x', label: 'X', peso: 10 },
  { key: 'qtd_10', label: '10', peso: 10 },
  { key: 'qtd_9', label: '9', peso: 9 },
  { key: 'qtd_8', label: '8', peso: 8 },
  { key: 'qtd_7', label: '7', peso: 7 },
  { key: 'qtd_6', label: '6', peso: 6 },
  { key: 'qtd_5', label: '5', peso: 5 },
  { key: 'qtd_4', label: '4', peso: 4 },
  { key: 'qtd_3', label: '3', peso: 3 },
  { key: 'qtd_2', label: '2', peso: 2 },
  { key: 'qtd_1', label: '1', peso: 1 },
  { key: 'qtd_0', label: '0', peso: 0 },
]

function calcTotalPontosFront(form: FormPrecisaoState): number {
  let total = 0
  for (const f of PRECISAO_FIELDS) {
    total += (parseInt(form[f.key] || '0', 10) || 0) * f.peso
  }
  total -= (parseInt(form.qtd_penalidade || '0', 10) || 0) * PONTOS_POR_PENALIDADE
  return Math.max(0, total)
}

interface FormSilhuetaState {
  carneiro: string; peru: string; porco: string; galinha: string
}

const SILHUETA_DEFAULTS: FormSilhuetaState = { carneiro: '0', peru: '0', porco: '0', galinha: '0' }

function calcTotalSilhuetaFront(form: FormSilhuetaState): number {
  return (
    (parseInt(form.carneiro || '0', 10) || 0) +
    (parseInt(form.peru || '0', 10) || 0) +
    (parseInt(form.porco || '0', 10) || 0) +
    (parseInt(form.galinha || '0', 10) || 0)
  )
}

// ── Formulário IPSC ──────────────────────────────────────────────────────────

interface FormIpscState {
  qtd_a: string; qtd_c: string; qtd_d: string; qtd_miss: string
  qtd_penalidade: string; tempo_segundos: string
}

const IPSC_DEFAULTS: FormIpscState = {
  qtd_a: '0', qtd_c: '0', qtd_d: '0', qtd_miss: '0',
  qtd_penalidade: '0', tempo_segundos: '0',
}

// Pontos por zona conforme o Power Factor da modalidade (Major/Minor).
const ZONAS_IPSC: Record<'major' | 'minor', { a: number; c: number; d: number }> = {
  major: { a: 5, c: 4, d: 2 },
  minor: { a: 5, c: 3, d: 1 },
}

function calcPontosIpscFront(form: FormIpscState, powerFactor: 'major' | 'minor' | null | undefined): number {
  const zonas = ZONAS_IPSC[powerFactor === 'major' ? 'major' : 'minor']
  const pontos =
    (parseInt(form.qtd_a || '0', 10) || 0) * zonas.a +
    (parseInt(form.qtd_c || '0', 10) || 0) * zonas.c +
    (parseInt(form.qtd_d || '0', 10) || 0) * zonas.d
  const penalidades = (parseInt(form.qtd_penalidade || '0', 10) || 0) * PONTOS_POR_PENALIDADE
  return Math.max(0, pontos - penalidades)
}

function calcHitFactorFront(pontos: number, form: FormIpscState): number {
  const tempo = parseFloat(form.tempo_segundos || '0') || 0
  if (tempo <= 0) return 0
  return Math.round((pontos / tempo) * 10000) / 10000
}

// ── Input mobile-friendly com botoes +/- ─────────────────────────────────────

interface NumInputProps {
  label: string
  sub?: string
  value: string
  onChange: (v: string) => void
  highlight?: boolean
}

function NumInput({ label, sub, value, onChange, highlight }: NumInputProps) {
  const num = parseInt(value || '0', 10) || 0

  return (
    <div className={`flex flex-col items-center rounded-xl border ${highlight ? 'border-compumaq-dourado/40 bg-compumaq-dourado/10' : 'border-compumaq-dourado/25 bg-compumaq-pretoSuave'} p-2 select-none`}>
      <span className={`text-xs font-bold ${highlight ? 'text-compumaq-doradoClaro' : 'text-compumaq-textoSuave'} mb-1`}>{label}</span>
      {sub && <span className="text-[10px] text-compumaq-textoSuave mb-1">{sub}</span>}
      <div className="flex items-center gap-1 w-full justify-center">
        <button
          type="button"
          onClick={() => { if (num > 0) onChange(String(num - 1)) }}
          className="w-9 h-9 rounded-lg bg-compumaq-pretoSuave hover:bg-compumaq-pretoSuave active:bg-compumaq-pretoSuave text-compumaq-texto text-xl font-bold flex items-center justify-center transition-colors touch-manipulation"
          tabIndex={-1}
        >
          −
        </button>
        <input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={0}
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`w-12 text-center text-lg font-bold border rounded-lg py-1.5 focus:outline-none focus:ring-2 focus:ring-compumaq-dourado ${highlight ? 'text-compumaq-doradoClaro border-compumaq-dourado/30 bg-compumaq-dourado/10' : 'text-compumaq-texto border-compumaq-dourado/25'}`}
        />
        <button
          type="button"
          onClick={() => onChange(String(num + 1))}
          className="w-9 h-9 rounded-lg bg-compumaq-dourado/15 hover:bg-compumaq-dourado/20 active:bg-compumaq-dourado/30 text-compumaq-doradoClaro text-xl font-bold flex items-center justify-center transition-colors touch-manipulation"
          tabIndex={-1}
        >
          +
        </button>
      </div>
    </div>
  )
}

// ── Card de atirador (mobile) ─────────────────────────────────────────────────

interface AtiradorCardProps {
  item: MapaResultadoItem
  onLancar: (item: MapaResultadoItem) => void
  onReimprimir: (item: MapaResultadoItem) => void
  isAdmin: boolean
  btConnected: boolean
}

function AtiradorCard({ item, onLancar, onReimprimir, isAdmin, btConnected }: AtiradorCardProps) {
  // Atirador comum nao pode editar resultado ja lancado (apenas admin)
  const podeAcionar = !item.tem_resultado || isAdmin
  return (
    <div
      className={`rounded-xl border p-3 flex items-center gap-3 ${
        item.tem_resultado ? 'bg-green-50 border-green-200' : 'bg-compumaq-pretoSuave border-compumaq-dourado/25'
      }`}
    >
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${item.tem_resultado ? 'bg-green-500' : 'bg-compumaq-pretoSuave'}`} />
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${item.tem_resultado ? 'text-green-900' : 'text-compumaq-texto'}`}>
          {item.atirador_nome}
        </p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-xs text-compumaq-textoSuave truncate max-w-[120px]">{item.clube_nome}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${item.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
            {item.categoria}
          </span>
        </div>
        {item.tem_resultado && (
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-green-700">
              {item.modalidade_grupo === 'Precisao'
                ? `${item.total_pontos ?? 0} pts${item.qtd_x != null ? ` · ${item.qtd_x}x` : ''}`
                : item.modalidade_grupo === 'IPSC'
                ? `HF ${Number(item.hit_factor ?? 0).toFixed(4)} · ${item.total_pontos ?? 0} pts`
                : `${item.total ?? 0} acertos`}
            </span>
            <span className="text-xs text-compumaq-textoSuave">{formatDate(item.data_lancamento)}</span>
            {item.data_atualizacao && <span className="text-xs text-amber-500">editado</span>}
          </div>
        )}
      </div>
      {podeAcionar ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.tem_resultado && (
            <button
              type="button"
              title="Reimprimir cupom"
              onClick={e => { e.stopPropagation(); onReimprimir(item) }}
              className={`p-2 rounded-lg transition-colors touch-manipulation ${
                btConnected ? 'text-green-600 hover:bg-green-100' : 'text-compumaq-textoSuave hover:bg-compumaq-pretoSuave'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={() => onLancar(item)}
            className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-semibold transition-colors touch-manipulation ${
              item.tem_resultado
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-compumaq-dourado text-compumaq-texto hover:bg-compumaq-doradoClaro'
            }`}
          >
            {item.tem_resultado ? 'Editar' : 'Lancar'}
          </button>
        </div>
      ) : (
        <span className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium text-green-700 bg-green-100">
          Lancado
        </span>
      )}
    </div>
  )
}

// ── Componente principal ──────────────────────────────────────────────────────

export function ResultadosPage() {
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selModalidade, setSelModalidade] = useState<number | ''>('')
  const [selEtapa, setSelEtapa] = useState<number | ''>('')
  const [filtroNomeAtirador, setFiltroNomeAtirador] = useState('')

  const [formularioOpen, setFormularioOpen] = useState(false)
  const [itemSelecionado, setItemSelecionado] = useState<MapaResultadoItem | null>(null)
  const [clickCount, setClickCount] = useState<Record<string, number>>({})

  const [formPrecisao, setFormPrecisao] = useState<FormPrecisaoState>(PRECISAO_DEFAULTS)
  const [formSilhueta, setFormSilhueta] = useState<FormSilhuetaState>(SILHUETA_DEFAULTS)
  const [formIpsc, setFormIpsc] = useState<FormIpscState>(IPSC_DEFAULTS)

  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const isOnline = useNetworkStatus()
  const { addToast } = useToast()

  // ── Bluetooth printer state ─────────────────────────────────────────────────
  const [btConnected, setBtConnected] = useState(() => bluetoothPrinter.isConnected())
  const [btConnecting, setBtConnecting] = useState(false)
  const btSaved = bluetoothPrinter.savedDeviceName()
  const btSupported = bluetoothPrinter.isSupported()

  async function handleConnectBluetooth() {
    if (!btSupported) {
      addToast('Web Bluetooth não suportado. Use Chrome/Edge no celular.', 'error')
      return
    }
    if (btConnected) {
      bluetoothPrinter.disconnect()
      setBtConnected(false)
      addToast('Impressora desconectada', 'info')
      return
    }
    setBtConnecting(true)
    try {
      await bluetoothPrinter.connect()
      setBtConnected(true)
      addToast(`Impressora "${bluetoothPrinter.savedDeviceName()}" conectada!`, 'success')
    } catch (err: any) {
      const msg = err?.message ?? 'Erro ao conectar impressora Bluetooth'
      if (!msg.includes('cancelled') && !msg.includes('chosen')) {
        addToast(msg, 'error')
      }
    } finally {
      setBtConnecting(false)
    }
  }

  // Auto-save draft ao localStorage
  const DRAFT_KEY = 'resultado_draft'
  useEffect(() => {
    if (formularioOpen && (itemSelecionado || selEtapa)) {
      const draft = JSON.stringify({ formPrecisao, formSilhueta, selEtapa, idinscricao: itemSelecionado?.idinscricao })
      localStorage.setItem(DRAFT_KEY, draft)
    }
  }, [formPrecisao, formSilhueta, formularioOpen, selEtapa, itemSelecionado])

  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY)
    if (saved) {
      try {
        const draft = JSON.parse(saved)
        if (draft.formPrecisao) setFormPrecisao(draft.formPrecisao)
        if (draft.formSilhueta) setFormSilhueta(draft.formSilhueta)
      } catch { /* ignore */ }
    }
  }, [])

  function clearDraft() {
    localStorage.removeItem(DRAFT_KEY)
  }

  // Limpa a busca por nome ao trocar de etapa
  useEffect(() => { setFiltroNomeAtirador('') }, [selEtapa])

  // Bloqueia scroll do body quando formulario esta aberto
  useEffect(() => {
    document.body.style.overflow = formularioOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [formularioOpen])

  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')

  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: modalidadesData } = useModalidades(
    selCampeonato ? { idcampeonato: selCampeonato as number, limit: 100 } : {},
  )
  const modalidades = selCampeonato ? (modalidadesData?.data ?? []) : []

  const { data: etapasData, isLoading: etapasLoading } = useQuery({
    queryKey: ['etapas-publicas', selCampeonato, selModalidade],
    queryFn: async () => {
      const { data } = await api.get<{ data: Etapa[] }>('/publico/etapas', {
        params: {
          idcampeonato: selCampeonato,
          idmodalidade: selModalidade,
        },
      })
      return data
    },
    enabled: !!selCampeonato && !!selModalidade,
  })
  const etapas = selCampeonato && selModalidade ? (etapasData?.data ?? []) : []

  const { data: mapaData, isLoading: mapaLoading } = useMapaResultados(
    selEtapa !== '' ? (selEtapa as number) : null,
  )

  const createResultado = useCreateResultado()
  const updateResultado = useUpdateResultado()
  const reprocessarResultados = useReprocessarResultados()

  const modalidadeSelecionada = modalidades.find(m => m.idmodalidade === selModalidade)

  const mapaFiltrado = filtroNomeAtirador.trim()
    ? (mapaData ?? []).filter(item =>
        item.atirador_nome?.toLowerCase().includes(filtroNomeAtirador.trim().toLowerCase()),
      )
    : (mapaData ?? [])

  const gruposPorModalidade = mapaFiltrado.reduce<Record<string, MapaResultadoItem[]>>((acc, item) => {
    const k = item.modalidade_nome
    if (!acc[k]) acc[k] = []
    acc[k].push(item)
    return acc
  }, {})

  function handleModalidadeChange(val: string) {
    const id = val ? parseInt(val) : ''
    setSelModalidade(id)
    setSelEtapa('')
  }

  // Click simples no mobile, duplo clique no desktop
  function handleItemClick(item: MapaResultadoItem) {
    const isMobile = window.matchMedia('(max-width: 767px)').matches
    if (isMobile) {
      openFormulario(item)
      return
    }
    const key = String(item.idinscricao)
    const now = Date.now()
    const lastClick = clickCount[key] ?? 0
    if (now - lastClick < 400) {
      openFormulario(item)
      setClickCount(prev => ({ ...prev, [key]: 0 }))
    } else {
      setClickCount(prev => ({ ...prev, [key]: now }))
    }
  }

  function openFormulario(item: MapaResultadoItem) {
    // Atirador comum nao pode editar resultado ja lancado
    if (item.tem_resultado && !isAdmin) {
      addToast('Apenas administradores podem editar resultados ja lancados', 'error')
      return
    }
    setItemSelecionado(item)

    if (item.tem_resultado) {
      const grupo = item.modalidade_grupo
      if (grupo === 'Precisao') {
        setFormPrecisao({
          qtd_x:  String(item.qtd_x  ?? 0),
          qtd_10: String(item.qtd_10 ?? 0),
          qtd_9:  String(item.qtd_9  ?? 0),
          qtd_8:  String(item.qtd_8  ?? 0),
          qtd_7:  String(item.qtd_7  ?? 0),
          qtd_6:  String(item.qtd_6  ?? 0),
          qtd_5:  String(item.qtd_5  ?? 0),
          qtd_4:  String(item.qtd_4  ?? 0),
          qtd_3:  String(item.qtd_3  ?? 0),
          qtd_2:  String(item.qtd_2  ?? 0),
          qtd_1:  String(item.qtd_1  ?? 0),
          qtd_0:  String(item.qtd_0  ?? 0),
          qtd_penalidade: String(item.qtd_penalidade ?? 0),
        })
      } else if (grupo === 'Silhueta') {
        setFormSilhueta({
          carneiro: String(item.carneiro ?? 0),
          peru: String(item.peru ?? 0),
          porco: String(item.porco ?? 0),
          galinha: String(item.galinha ?? 0),
        })
      } else if (grupo === 'IPSC') {
        setFormIpsc({
          qtd_a: String(item.qtd_a ?? 0),
          qtd_c: String(item.qtd_c ?? 0),
          qtd_d: String(item.qtd_d ?? 0),
          qtd_miss: String(item.qtd_miss ?? 0),
          qtd_penalidade: String(item.qtd_penalidade ?? 0),
          tempo_segundos: String(item.tempo_segundos ?? 0),
        })
      }
    } else {
      setFormPrecisao(PRECISAO_DEFAULTS)
      setFormSilhueta(SILHUETA_DEFAULTS)
      setFormIpsc(IPSC_DEFAULTS)
    }

    setFormularioOpen(true)
  }

  function buildPayloadPorGrupo(grupo: string) {
    if (grupo === 'Precisao') {
      return {
        qtd_x: parseInt(formPrecisao.qtd_x || '0'),
        qtd_10: parseInt(formPrecisao.qtd_10 || '0'),
        qtd_9: parseInt(formPrecisao.qtd_9 || '0'),
        qtd_8: parseInt(formPrecisao.qtd_8 || '0'),
        qtd_7: parseInt(formPrecisao.qtd_7 || '0'),
        qtd_6: parseInt(formPrecisao.qtd_6 || '0'),
        qtd_5: parseInt(formPrecisao.qtd_5 || '0'),
        qtd_4: parseInt(formPrecisao.qtd_4 || '0'),
        qtd_3: parseInt(formPrecisao.qtd_3 || '0'),
        qtd_2: parseInt(formPrecisao.qtd_2 || '0'),
        qtd_1: parseInt(formPrecisao.qtd_1 || '0'),
        qtd_0: parseInt(formPrecisao.qtd_0 || '0'),
        qtd_penalidade: parseInt(formPrecisao.qtd_penalidade || '0'),
      }
    }
    if (grupo === 'IPSC') {
      return {
        qtd_a: parseInt(formIpsc.qtd_a || '0'),
        qtd_c: parseInt(formIpsc.qtd_c || '0'),
        qtd_d: parseInt(formIpsc.qtd_d || '0'),
        qtd_miss: parseInt(formIpsc.qtd_miss || '0'),
        qtd_penalidade: parseInt(formIpsc.qtd_penalidade || '0'),
        tempo_segundos: parseFloat(formIpsc.tempo_segundos || '0') || 0,
      }
    }
    return {
      carneiro: parseInt(formSilhueta.carneiro || '0'),
      peru: parseInt(formSilhueta.peru || '0'),
      porco: parseInt(formSilhueta.porco || '0'),
      galinha: parseInt(formSilhueta.galinha || '0'),
    }
  }

  async function executarSalvar(): Promise<{ grupo: string; totalPontos: number; totalAcertos: number; qtdX: number; hitFactor: number } | null> {
    if (!itemSelecionado) return null
    const grupo = itemSelecionado.modalidade_grupo

    if (itemSelecionado.tem_resultado && itemSelecionado.idresultado) {
      const payload = buildPayloadPorGrupo(grupo)
      await updateResultado.mutateAsync({ id: itemSelecionado.idresultado, data: payload })
      setAlert({ type: 'success', message: `Resultado de ${itemSelecionado.atirador_nome} atualizado` })
      clearDraft()
    } else {
      const payload = { idinscricao: itemSelecionado.idinscricao, ...buildPayloadPorGrupo(grupo) }
      await createResultado.mutateAsync(payload)
      setAlert({ type: 'success', message: `Resultado de ${itemSelecionado.atirador_nome} lancado com sucesso` })
      clearDraft()
    }

    const pontosIpsc = calcPontosIpscFront(formIpsc, modalidadeSelecionada?.power_factor)

    return {
      grupo,
      totalPontos: grupo === 'IPSC' ? pontosIpsc : calcTotalPontosFront(formPrecisao),
      totalAcertos: calcTotalSilhuetaFront(formSilhueta),
      qtdX: parseInt(formPrecisao.qtd_x || '0'),
      hitFactor: grupo === 'IPSC' ? calcHitFactorFront(pontosIpsc, formIpsc) : 0,
    }
  }

  async function handleSalvar() {
    try {
      await executarSalvar()
      setFormularioOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar resultado' })
    }
  }

  async function handleSalvarEImprimir() {
    if (!itemSelecionado) return
    try {
      const info = await executarSalvar()
      if (!info) return
      setFormularioOpen(false)

      const etapas2 = etapasData?.data ?? []
      const etapaSel = etapas2.find(e => e.idetapa === selEtapa)
      const divisaoInfo = modalidadeSelecionada?.divisao ?? ''
      const numDisparos = modalidadeSelecionada?.numero_alvo_serie
        ? `${modalidadeSelecionada.numero_alvo_serie} DISPAROS`
        : ''
      const armaDistancia = divisaoInfo
        ? `${divisaoInfo.toUpperCase()}${numDisparos ? ' ' + numDisparos : ''}`
        : numDisparos

      const qtdX  = parseInt(formPrecisao.qtd_x  || '0')
      const qtd10 = parseInt(formPrecisao.qtd_10 || '0')
      const qtd9  = parseInt(formPrecisao.qtd_9  || '0')
      const qtd8  = parseInt(formPrecisao.qtd_8  || '0')
      const qtd7  = parseInt(formPrecisao.qtd_7  || '0')
      const qtd6  = parseInt(formPrecisao.qtd_6  || '0')
      const qtd5  = parseInt(formPrecisao.qtd_5  || '0')
      const qtd4  = parseInt(formPrecisao.qtd_4  || '0')
      const qtd3  = parseInt(formPrecisao.qtd_3  || '0')
      const qtd2  = parseInt(formPrecisao.qtd_2  || '0')
      const qtd1  = parseInt(formPrecisao.qtd_1  || '0')
      const qtd0  = parseInt(formPrecisao.qtd_0  || '0')
      const totalPenalidades = qtd0

      // ── Impressão Bluetooth direta (sem diálogo) ────────────────────────
      if (btConnected || bluetoothPrinter.isConnected()) {
        const printOpts: PrintResultadoOpts = {
          atirador_nome:   itemSelecionado.atirador_nome,
          modalidade_nome: itemSelecionado.modalidade_nome,
          grupo:           info.grupo,
          armaDistancia,
          qtd_x: qtdX, qtd_10: qtd10, qtd_9: qtd9, qtd_8: qtd8,
          qtd_7: qtd7, qtd_6: qtd6, qtd_5: qtd5, qtd_4: qtd4,
          qtd_3: qtd3, qtd_2: qtd2, qtd_1: qtd1, qtd_0: qtd0,
          totalPontos:      info.totalPontos,
          totalPenalidades,
          galinha:  parseInt(formSilhueta.galinha  || '0'),
          porco:    parseInt(formSilhueta.porco    || '0'),
          peru:     parseInt(formSilhueta.peru     || '0'),
          carneiro: parseInt(formSilhueta.carneiro || '0'),
          totalAcertos: info.totalAcertos,
        }
        try {
          await bluetoothPrinter.printResultado(printOpts)
          setBtConnected(bluetoothPrinter.isConnected())
          return
        } catch (btErr: any) {
          setBtConnected(false)
          addToast(`Bluetooth: ${btErr?.message ?? 'Erro'} — usando impressão padrão`, 'error')
          // cai no fallback abaixo
        }
      }

      // ── Fallback: window.print() via iframe ────────────────────────────
      const conteudo = `
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8" />
          <title>Comprovante de Resultado</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px;
              color: #000;
              background: #fff;
              padding: 4px 4px;
              max-width: 80mm;
              width: 80mm;
              margin: 0;
              line-height: 1.3;
            }
            @media print {
              @page { size: 80mm auto; margin: 0; }
              * { margin: 0; padding: 0; }
              body { width: 80mm; max-width: 80mm; padding: 2px 2px; }
              .no-print { display: none !important; }
            }
            .center { text-align: center; }
            .left   { text-align: left; }
            .sep    { border-top: 1px dashed #000; margin: 4px 0; }
            .sep-solid { border-top: 1px solid #000; margin: 4px 0; }
            .nome   { font-size: 14px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 2px; word-break: break-word; }
            .modalidade { font-size: 12px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 1px; }
            .arma   { font-size: 12px; text-align: center; text-transform: uppercase; margin-bottom: 3px; }
            .serie-titulo { font-size: 12px; font-weight: bold; text-align: center; margin: 3px 0 2px; text-transform: uppercase; }
            .grid   { width: 100%; border-collapse: collapse; margin: 2px 0; }
            .grid td { text-align: center; font-size: 12px; font-weight: bold; padding: 2px 1px; border: 1px solid #000; }
            .totais { margin-top: 4px; font-size: 12px; font-weight: bold; }
            .totais p { margin: 2px 0; }
            .total-pts { font-size: 13px; font-weight: bold; text-transform: uppercase; }
            .rodape { font-size: 11px; text-align: center; margin-top: 6px; }
            .assin { margin-top: 8px; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
          </style>
        </head>
        <body>
          <div class="center" style="font-size:11px; margin-bottom:2px; text-transform:uppercase;">CAMPEONATO DE TIRO</div>
          <div class="sep-solid"></div>

          <div class="nome">${itemSelecionado.atirador_nome}</div>
          <div class="modalidade">${itemSelecionado.modalidade_nome}</div>
          ${armaDistancia ? `<div class="arma">${armaDistancia}</div>` : ''}

          <div class="sep"></div>

          ${info.grupo === 'Precisao' ? `
          <div class="serie-titulo">SERIE 1</div>
          <table class="grid">
            <tr>
              <td>X=${qtdX}</td>
              <td>10=${qtd10}</td>
              <td>9=${qtd9}</td>
              <td>8=${qtd8}</td>
            </tr>
            <tr>
              <td>7=${qtd7}</td>
              <td>6=${qtd6}</td>
              <td>5=${qtd5}</td>
              <td>4=${qtd4}</td>
            </tr>
            <tr>
              <td>3=${qtd3}</td>
              <td>2=${qtd2}</td>
              <td>1=${qtd1}</td>
              <td>0=${qtd0}</td>
            </tr>
          </table>

          <div class="sep"></div>

          <div class="totais">
            <p>TOTAL DE PENALIDADES = ${totalPenalidades}</p>
            <p class="total-pts">TOTAL DE PONTOS = ${info.totalPontos}</p>
          </div>
          ` : info.grupo === 'IPSC' ? `
          <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">A = ${formIpsc.qtd_a}  C = ${formIpsc.qtd_c}  D = ${formIpsc.qtd_d}  MISS = ${formIpsc.qtd_miss}</p>
          <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PENALIDADES = ${formIpsc.qtd_penalidade}</p>
          <p class="serie-titulo" style="text-align:left; margin-bottom:4px;">TEMPO = ${formIpsc.tempo_segundos}s</p>

          <div class="sep"></div>

          <div class="totais">
            <p>PONTOS = ${info.totalPontos}</p>
            <p class="total-pts">HIT FACTOR = ${info.hitFactor.toFixed(4)}</p>
          </div>
          ` : `
          <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">GALINHA = ${formSilhueta.galinha}</p>
          <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PORCO = ${formSilhueta.porco}</p>
          <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PERU = ${formSilhueta.peru}</p>
          <p class="serie-titulo" style="text-align:left; margin-bottom:4px;">CARNEIRO = ${formSilhueta.carneiro}</p>

          <div class="sep"></div>

          <div class="totais">
            <p class="total-pts">PONTOS = ${info.totalAcertos}</p>
          </div>
          `}

          <div class="sep"></div>

          <div class="rodape">${new Date().toLocaleString('pt-BR')}</div>

          <div class="assin">
            <p>Assinatura: _______________________________</p>
          </div>

        </body>
        </html>
      `

      const existingFrame = document.getElementById('__thermal_print_frame')
      if (existingFrame) existingFrame.remove()

      const frame = document.createElement('iframe')
      frame.id = '__thermal_print_frame'
      frame.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden;'
      document.body.appendChild(frame)

      const frameDoc = frame.contentDocument || frame.contentWindow?.document
      if (frameDoc) {
        frameDoc.open()
        frameDoc.write(conteudo)
        frameDoc.close()
      }

      setTimeout(() => {
        try {
          frame.contentWindow?.focus()
          frame.contentWindow?.print()
        } finally {
          setTimeout(() => frame.remove(), 2000)
        }
      }, 400)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar resultado' })
    }
  }

  async function handleReimprimir(item: MapaResultadoItem) {
    if (!item.tem_resultado) return

    const etapaAtual = etapasData?.data ?? []
    const etapaSel = etapaAtual.find(e => e.idetapa === selEtapa)
    const modalidadeItem = modalidades.find(m => m.idmodalidade === selModalidade) ?? modalidadeSelecionada
    const divisaoInfo = modalidadeItem?.divisao ?? ''
    const numDisparos = modalidadeItem?.numero_alvo_serie
      ? `${modalidadeItem.numero_alvo_serie} DISPAROS`
      : ''
    const armaDistancia = divisaoInfo
      ? `${divisaoInfo.toUpperCase()}${numDisparos ? ' ' + numDisparos : ''}`
      : numDisparos

    const qtdX  = item.qtd_x  ?? 0
    const qtd10 = item.qtd_10 ?? 0
    const qtd9  = item.qtd_9  ?? 0
    const qtd8  = item.qtd_8  ?? 0
    const qtd7  = item.qtd_7  ?? 0
    const qtd6  = item.qtd_6  ?? 0
    const qtd5  = item.qtd_5  ?? 0
    const qtd4  = item.qtd_4  ?? 0
    const qtd3  = item.qtd_3  ?? 0
    const qtd2  = item.qtd_2  ?? 0
    const qtd1  = item.qtd_1  ?? 0
    const qtd0  = item.qtd_0  ?? 0

    const totalPontos    = item.total_pontos   ?? 0
    const totalAcertos   = item.total          ?? 0
    const totalPenalidades = qtd0

    // ── Impressão Bluetooth direta ──────────────────────────────────────────
    if (btConnected || bluetoothPrinter.isConnected()) {
      const printOpts: PrintResultadoOpts = {
        atirador_nome:   item.atirador_nome,
        modalidade_nome: item.modalidade_nome,
        grupo:           item.modalidade_grupo,
        armaDistancia,
        qtd_x:  qtdX,
        qtd_10: qtd10, qtd_9: qtd9, qtd_8: qtd8, qtd_7: qtd7,
        qtd_6:  qtd6,  qtd_5: qtd5, qtd_4: qtd4, qtd_3: qtd3,
        qtd_2:  qtd2,  qtd_1: qtd1, qtd_0: qtd0,
        totalPontos,
        totalPenalidades,
        galinha:  item.galinha  ?? 0,
        porco:    item.porco    ?? 0,
        peru:     item.peru     ?? 0,
        carneiro: item.carneiro ?? 0,
        totalAcertos,
      }
      try {
        await bluetoothPrinter.printResultado(printOpts)
        setBtConnected(bluetoothPrinter.isConnected())
        addToast(`Reimpressao enviada para impressora Bluetooth`, 'success')
        return
      } catch (btErr: any) {
        setBtConnected(false)
        addToast(`Bluetooth: ${btErr?.message ?? 'Erro'} — usando impressao padrao`, 'error')
        // cai no fallback abaixo
      }
    }

    // ── Fallback: window.print() via iframe ──────────────────────────────
    const grupo = item.modalidade_grupo
    const conteudo = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Comprovante de Resultado</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', Courier, monospace;
            font-size: 12px;
            color: #000;
            background: #fff;
            padding: 4px 4px;
            max-width: 80mm;
            width: 80mm;
            margin: 0;
            line-height: 1.3;
          }
          @media print {
            @page { size: 80mm auto; margin: 0; }
            * { margin: 0; padding: 0; }
            body { width: 80mm; max-width: 80mm; padding: 2px 2px; }
            .no-print { display: none !important; }
          }
          .center { text-align: center; }
          .left   { text-align: left; }
          .sep    { border-top: 1px dashed #000; margin: 4px 0; }
          .sep-solid { border-top: 1px solid #000; margin: 4px 0; }
          .nome   { font-size: 14px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 2px; word-break: break-word; }
          .modalidade { font-size: 12px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 1px; }
          .arma   { font-size: 12px; text-align: center; text-transform: uppercase; margin-bottom: 3px; }
          .serie-titulo { font-size: 12px; font-weight: bold; text-align: center; margin: 3px 0 2px; text-transform: uppercase; }
          .grid   { width: 100%; border-collapse: collapse; margin: 2px 0; }
          .grid td { text-align: center; font-size: 12px; font-weight: bold; padding: 2px 1px; border: 1px solid #000; }
          .totais { margin-top: 4px; font-size: 12px; font-weight: bold; }
          .totais p { margin: 2px 0; }
          .total-pts { font-size: 13px; font-weight: bold; text-transform: uppercase; }
          .rodape { font-size: 11px; text-align: center; margin-top: 6px; }
          .assin { margin-top: 8px; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="center" style="font-size:11px; margin-bottom:2px; text-transform:uppercase;">CAMPEONATO DE TIRO</div>
        <div class="sep-solid"></div>

        <div class="nome">${item.atirador_nome}</div>
        <div class="modalidade">${item.modalidade_nome}</div>
        ${armaDistancia ? `<div class="arma">${armaDistancia}</div>` : ''}

        <div class="sep"></div>

        ${grupo === 'Precisao' ? `
        <div class="serie-titulo">SERIE 1</div>
        <table class="grid">
          <tr>
            <td>X=${qtdX}</td>
            <td>10=${qtd10}</td>
            <td>9=${qtd9}</td>
            <td>8=${qtd8}</td>
          </tr>
          <tr>
            <td>7=${qtd7}</td>
            <td>6=${qtd6}</td>
            <td>5=${qtd5}</td>
            <td>4=${qtd4}</td>
          </tr>
          <tr>
            <td>3=${qtd3}</td>
            <td>2=${qtd2}</td>
            <td>1=${qtd1}</td>
            <td>0=${qtd0}</td>
          </tr>
        </table>

        <div class="sep"></div>

        <div class="totais">
          <p>TOTAL DE PENALIDADES = ${totalPenalidades}</p>
          <p class="total-pts">TOTAL DE PONTOS = ${totalPontos}</p>
        </div>
        ` : grupo === 'IPSC' ? `
        <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">A = ${item.qtd_a ?? 0}  C = ${item.qtd_c ?? 0}  D = ${item.qtd_d ?? 0}  MISS = ${item.qtd_miss ?? 0}</p>
        <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PENALIDADES = ${item.qtd_penalidade ?? 0}</p>
        <p class="serie-titulo" style="text-align:left; margin-bottom:4px;">TEMPO = ${item.tempo_segundos ?? 0}s</p>

        <div class="sep"></div>

        <div class="totais">
          <p>PONTOS = ${totalPontos}</p>
          <p class="total-pts">HIT FACTOR = ${Number(item.hit_factor ?? 0).toFixed(4)}</p>
        </div>
        ` : `
        <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">GALINHA = ${item.galinha ?? 0}</p>
        <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PORCO = ${item.porco ?? 0}</p>
        <p class="serie-titulo" style="text-align:left; margin-bottom:2px;">PERU = ${item.peru ?? 0}</p>
        <p class="serie-titulo" style="text-align:left; margin-bottom:4px;">CARNEIRO = ${item.carneiro ?? 0}</p>

        <div class="sep"></div>

        <div class="totais">
          <p class="total-pts">PONTOS = ${totalAcertos}</p>
        </div>
        `}

        <div class="sep"></div>

        <div class="rodape">${new Date().toLocaleString('pt-BR')}</div>

        <div class="assin">
          <p>Assinatura: _______________________________</p>
        </div>

      </body>
      </html>
    `

    const existingFrame = document.getElementById('__thermal_print_frame')
    if (existingFrame) existingFrame.remove()

    const frame = document.createElement('iframe')
    frame.id = '__thermal_print_frame'
    frame.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden;'
    document.body.appendChild(frame)

    const frameDoc = frame.contentDocument || frame.contentWindow?.document
    if (frameDoc) {
      frameDoc.open()
      frameDoc.write(conteudo)
      frameDoc.close()
    }

    setTimeout(() => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } finally {
        setTimeout(() => frame.remove(), 2000)
      }
    }, 400)
  }

  async function handleSalvarEImprimirThermal() {
    if (!itemSelecionado) return
    try {
      const info = await executarSalvar()
      if (!info) return
      setFormularioOpen(false)

      const etapas2 = etapasData?.data ?? []
      const etapaSel = etapas2.find(e => e.idetapa === selEtapa)
      const etapaTitulo = etapaSel?.titulo_reduzido || etapaSel?.titulo || `Etapa ${etapaSel?.numero_etapa ?? ''}`
      const campeonatoSelecionado = (campeonatosData?.data ?? []).find(c => c.idcampeonato === selCampeonato)
      const campeonatoDesc = campeonatoSelecionado ? `${campeonatoSelecionado.descricao} — ${campeonatoSelecionado.temporada}` : ''

      printComprovanteResultadoThermal({
        atirador_nome: itemSelecionado.atirador_nome,
        cpf: itemSelecionado.atirador_cpf,
        campeonato_descricao: campeonatoDesc,
        etapa_titulo: etapaTitulo,
        modalidade_nome: `${itemSelecionado.modalidade_nome} (${itemSelecionado.modalidade_grupo})`,
        total_pontos: info.grupo === 'Precisao' || info.grupo === 'IPSC' ? info.totalPontos : info.totalAcertos,
        qtd_x: info.qtdX,
        data: new Date().toLocaleString('pt-BR'),
      })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar resultado' })
    }
  }

  const totalLancados = (mapaData ?? []).filter(i => i.tem_resultado).length
  const totalPendentes = (mapaData ?? []).filter(i => !i.tem_resultado).length
  const isPending = createResultado.isPending || updateResultado.isPending

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mapa de Resultados"
        description="Lance e gerencie os resultados por etapa."
      />

      {/* Botão Conectar Impressora Bluetooth */}
      {btSupported && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleConnectBluetooth}
            disabled={btConnecting}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors touch-manipulation ${
              btConnected
                ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                : 'bg-compumaq-pretoSuave border-compumaq-dourado/25 text-compumaq-texto hover:bg-compumaq-preto'
            } disabled:opacity-60`}
          >
            {/* ícone Bluetooth */}
            <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 shrink-0 ${btConnected ? 'text-green-600' : 'text-compumaq-doradoClaro'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5" />
            </svg>
            {btConnecting
              ? 'Conectando...'
              : btConnected
                ? `Impressora: ${bluetoothPrinter.savedDeviceName() ?? 'Conectada'}`
                : btSaved
                  ? `Reconectar: ${btSaved}`
                  : 'Conectar Impressora Bluetooth'
            }
          </button>
          {btConnected && (
            <span className="flex items-center gap-1.5 text-xs text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse" />
              Conectada
            </span>
          )}
        </div>
      )}

      {/* Indicador offline */}
      {!isOnline && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-3 text-yellow-800 text-sm">
          <span className="text-xl">📴</span>
          <div>
            <span className="font-semibold">Modo Offline</span>
            <p className="text-yellow-600 text-xs">Os resultados serao salvos localmente e sincronizados quando a conexao voltar.</p>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Filtros em cascata */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select
              className={selectClass}
              value={selCampeonato}
              onChange={e => {
                setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                setSelModalidade('')
                setSelEtapa('')
              }}
            >
              <option value="">Selecione...</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
            <select
              className={selectClass}
              value={selModalidade}
              onChange={e => handleModalidadeChange(e.target.value)}
              disabled={!selCampeonato}
            >
              <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
              {modalidades.map(m => (
                <option key={m.idmodalidade} value={m.idmodalidade}>
                  {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
            <select
              className={selectClass}
              value={selEtapa}
              onChange={e => setSelEtapa(e.target.value ? parseInt(e.target.value) : '')}
              disabled={!selModalidade || etapasLoading}
            >
              <option value="">
                {etapasLoading ? 'Carregando...' : selModalidade ? 'Selecione...' : '— selecione a modalidade —'}
              </option>
              {etapas.map(e => (
                <option key={e.idetapa} value={e.idetapa}>{e.titulo_reduzido || `Etapa ${e.numero_etapa}`}</option>
              ))}
            </select>
            {!etapasLoading && selModalidade && etapas.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Nenhuma etapa encontrada para esta modalidade.</p>
            )}
          </div>
        </div>

        {/* Botão reprocessar (apenas Precisao) */}
        {selModalidade !== '' && modalidadeSelecionada?.grupo === 'Precisao' && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Reprocessar todos os resultados desta modalidade com o valor X atual?')) return
                try {
                  const res = await reprocessarResultados.mutateAsync(selModalidade as number)
                  setAlert({ type: 'success', message: `${res.atualizados} resultado(s) reprocessado(s) com sucesso.` })
                } catch (err: any) {
                  setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao reprocessar resultados' })
                }
              }}
              disabled={reprocessarResultados.isPending}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium touch-manipulation"
            >
              {reprocessarResultados.isPending ? 'Reprocessando...' : 'Reprocessar Resultados'}
            </button>
          </div>
        )}
      </div>

      {/* Indicadores de progresso */}
      {selEtapa !== '' && !mapaLoading && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-compumaq-texto">Progresso de lancamentos</span>
            <span className="text-compumaq-textoSuave">
              <span className="font-semibold text-green-600">{totalLancados}</span>
              {' / '}
              <span className="font-semibold text-compumaq-texto">{totalLancados + totalPendentes}</span>
              {' '}
              ({totalLancados + totalPendentes > 0
                ? Math.round((totalLancados / (totalLancados + totalPendentes)) * 100)
                : 0}%)
            </span>
          </div>
          <div className="w-full bg-compumaq-pretoSuave rounded-full h-3 overflow-hidden">
            <div
              className="bg-green-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{
                width: totalLancados + totalPendentes > 0
                  ? `${(totalLancados / (totalLancados + totalPendentes)) * 100}%`
                  : '0%',
              }}
            />
          </div>
          <div className="flex gap-4 text-xs text-compumaq-textoSuave">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
              {totalLancados} lancados
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-compumaq-pretoSuave inline-block" />
              {totalPendentes} pendentes
            </span>
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {selEtapa === '' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
          <p className="text-3xl mb-2">🎯</p>
          <p className="font-medium">Selecione um campeonato, modalidade e etapa para ver o mapa</p>
        </div>
      )}

      {/* Loading */}
      {selEtapa !== '' && mapaLoading && (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      )}

      {/* Busca por nome do atirador */}
      {selEtapa !== '' && !mapaLoading && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-3 shadow-sm">
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar atirador</label>
          <input
            type="text"
            placeholder="Digite o nome do atirador..."
            value={filtroNomeAtirador}
            onChange={e => setFiltroNomeAtirador(e.target.value)}
            className="w-full border border-compumaq-dourado/25 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-compumaq-dourado focus:border-compumaq-dourado"
          />
        </div>
      )}

      {/* Mapa de resultados */}
      {selEtapa !== '' && !mapaLoading && (
        <div className="space-y-4">
          {filtroNomeAtirador.trim() && mapaFiltrado.length === 0 && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-10 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">🔍</p>
              <p className="font-medium">Nenhum atirador encontrado para "{filtroNomeAtirador}"</p>
            </div>
          )}
          {Object.entries(gruposPorModalidade).map(([modalidadeNome, items]) => (
            <div key={modalidadeNome} className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
              {/* Header do grupo */}
              <div className="bg-compumaq-preto border-b border-compumaq-dourado/25 px-4 py-3 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-compumaq-texto">{modalidadeNome}</span>
                  <span className="ml-2 text-xs text-compumaq-textoSuave">({items[0]?.modalidade_grupo})</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-compumaq-textoSuave">
                  <span className="text-green-600 font-medium">{items.filter(i => i.tem_resultado).length} lancados</span>
                  <span>/ {items.length} inscritos</span>
                </div>
              </div>

              {/* Cards mobile */}
              <div className="md:hidden p-3 space-y-2">
                {items.map(item => (
                  <AtiradorCard key={item.idinscricao} item={item} onLancar={openFormulario} onReimprimir={handleReimprimir} isAdmin={isAdmin} btConnected={btConnected} />
                ))}
              </div>

              {/* Tabela desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-compumaq-textoSuave w-8">Status</th>
                      <th className="text-left px-4 py-2 font-medium text-compumaq-textoSuave">Atirador</th>
                      <th className="text-left px-4 py-2 font-medium text-compumaq-textoSuave">Clube</th>
                      <th className="text-left px-4 py-2 font-medium text-compumaq-textoSuave">Cat.</th>
                      <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">
                        {items[0]?.modalidade_grupo === 'Precisao' ? 'Total Pts' : items[0]?.modalidade_grupo === 'IPSC' ? 'Hit Factor' : 'Total'}
                      </th>
                      {items[0]?.modalidade_grupo === 'Precisao' && (
                        <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">X</th>
                      )}
                      {items[0]?.modalidade_grupo === 'Silhueta' && (
                        <>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Galinha</th>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Porco</th>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Peru</th>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Carneiro</th>
                        </>
                      )}
                      {items[0]?.modalidade_grupo === 'IPSC' && (
                        <>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Pontos</th>
                          <th className="text-right px-4 py-2 font-medium text-compumaq-textoSuave">Tempo</th>
                        </>
                      )}
                      <th className="text-left px-4 py-2 font-medium text-compumaq-textoSuave">Lancamento</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-compumaq-dourado/25">
                    {items.map(item => {
                      const podeAcionar = !item.tem_resultado || isAdmin
                      return (
                      <tr
                        key={item.idinscricao}
                        className={`transition-colors ${item.tem_resultado ? 'bg-green-50/30' : ''} ${podeAcionar ? 'hover:bg-compumaq-dourado/10 cursor-pointer' : 'cursor-default'}`}
                        onClick={() => { if (podeAcionar) handleItemClick(item) }}
                        title={podeAcionar ? 'Duplo clique para lancar/editar resultado' : 'Resultado ja lancado (somente admin pode editar)'}
                      >
                        <td className="px-4 py-3">
                          <div
                            className={`w-3 h-3 rounded-full ${item.tem_resultado ? 'bg-green-500' : 'bg-compumaq-pretoSuave'}`}
                            title={item.tem_resultado ? 'Resultado lancado' : 'Pendente'}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <p className={`font-medium ${item.tem_resultado ? 'text-green-800' : 'text-compumaq-texto'}`}>
                            {item.atirador_nome}
                          </p>
                          <p className="text-xs text-compumaq-textoSuave font-mono">{item.atirador_cpf}</p>
                        </td>
                        <td className="px-4 py-3 text-compumaq-textoSuave">{item.clube_nome}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
                            {item.categoria}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-compumaq-texto">
                          {item.tem_resultado
                            ? item.modalidade_grupo === 'Precisao' ? item.total_pontos
                              : item.modalidade_grupo === 'IPSC' ? Number(item.hit_factor ?? 0).toFixed(4)
                              : item.total ?? '—'
                            : <span className="text-compumaq-textoSuave">—</span>
                          }
                        </td>
                        {item.modalidade_grupo === 'Precisao' && (
                          <td className="px-4 py-3 text-right text-compumaq-textoSuave">
                            {item.tem_resultado ? (item.qtd_x ?? 0) : <span className="text-compumaq-textoSuave">—</span>}
                          </td>
                        )}
                        {item.modalidade_grupo === 'Silhueta' && (
                          <>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? (item.galinha ?? 0) : <span className="text-compumaq-textoSuave">—</span>}</td>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? (item.porco ?? 0) : <span className="text-compumaq-textoSuave">—</span>}</td>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? (item.peru ?? 0) : <span className="text-compumaq-textoSuave">—</span>}</td>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? (item.carneiro ?? 0) : <span className="text-compumaq-textoSuave">—</span>}</td>
                          </>
                        )}
                        {item.modalidade_grupo === 'IPSC' && (
                          <>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? (item.total_pontos ?? 0) : <span className="text-compumaq-textoSuave">—</span>}</td>
                            <td className="px-4 py-3 text-right text-compumaq-textoSuave">{item.tem_resultado ? `${item.tempo_segundos ?? 0}s` : <span className="text-compumaq-textoSuave">—</span>}</td>
                          </>
                        )}
                        <td className="px-4 py-3 text-xs text-compumaq-textoSuave">
                          {item.tem_resultado ? (
                            <div>
                              <p>{formatDate(item.data_lancamento)}</p>
                              {item.data_atualizacao && (
                                <p className="text-amber-500">Editado {formatDate(item.data_atualizacao)}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-compumaq-textoSuave">pendente</span>
                          )}
                        </td>
                        <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                          {item.tem_resultado && (
                            <button
                              type="button"
                              title="Reimprimir cupom deste resultado"
                              onClick={() => handleReimprimir(item)}
                              className={`p-1.5 rounded-lg transition-colors touch-manipulation ${
                                btConnected
                                  ? 'text-green-600 hover:bg-green-100'
                                  : 'text-compumaq-textoSuave hover:bg-compumaq-pretoSuave'
                              }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {Object.keys(gruposPorModalidade).length === 0 && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">📋</p>
              <p>Nenhuma inscricao encontrada nesta etapa</p>
            </div>
          )}
        </div>
      )}

      {/* ── Formulário: full-screen no mobile, modal no desktop ─────────── */}
      {formularioOpen && itemSelecionado && (
        <>
          {/* Overlay desktop (md+) */}
          <div
            className="hidden md:block fixed inset-0 z-40 bg-black/50"
            onClick={() => setFormularioOpen(false)}
          />

          {/* Painel — full-screen no mobile, modal centralizado no desktop */}
          <div className={`
            fixed z-50 bg-compumaq-pretoSuave overflow-y-auto
            inset-0
            md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2
            md:w-full md:max-w-2xl md:max-h-[90vh] md:rounded-xl md:shadow-xl
          `}>
            {/* Cabeçalho fixo */}
            <div className="sticky top-0 z-10 bg-compumaq-pretoSuave border-b border-compumaq-dourado/25 flex items-center justify-between px-4 py-3 md:px-6 md:py-4">
              <h2 className="text-base md:text-lg font-semibold text-compumaq-texto">
                {itemSelecionado.tem_resultado ? 'Editar Resultado' : 'Lancar Resultado'}
              </h2>
              <button
                onClick={() => setFormularioOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-compumaq-textoSuave hover:bg-compumaq-pretoSuave text-xl touch-manipulation"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            {/* Conteúdo com scroll */}
            <div className="p-4 md:p-6 space-y-4 pb-8">
              {/* Info do atirador */}
              <div className="p-3 bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-xl text-sm">
                <p className="font-bold text-compumaq-doradoClaro text-base mb-1">{itemSelecionado.atirador_nome}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-compumaq-textoSuave">
                  <span><span className="text-compumaq-textoSuave">Clube: </span>{itemSelecionado.clube_nome}</span>
                  <span><span className="text-compumaq-textoSuave">Cat.: </span>{itemSelecionado.categoria}</span>
                  <span className="col-span-2"><span className="text-compumaq-textoSuave">Modalidade: </span>{itemSelecionado.modalidade_nome} ({itemSelecionado.modalidade_grupo})</span>
                </div>
                {itemSelecionado.tem_resultado && itemSelecionado.data_lancamento && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    Lancado em: {formatDateTime(itemSelecionado.data_lancamento)}
                    {itemSelecionado.data_atualizacao && ` · Editado: ${formatDateTime(itemSelecionado.data_atualizacao)}`}
                  </p>
                )}
              </div>

              {/* Formulário Precisão */}
              {itemSelecionado.modalidade_grupo === 'Precisao' && (
                <div>
                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Pontuacao por zona</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {PRECISAO_FIELDS.map(f => (
                      <NumInput
                        key={f.key}
                        label={f.label}
                        sub={`${f.peso} pts`}
                        value={formPrecisao[f.key]}
                        onChange={v => setFormPrecisao(prev => ({ ...prev, [f.key]: v }))}
                        highlight={f.key === 'qtd_x'}
                      />
                    ))}
                  </div>

                  {modalidadeTemPenalidade(itemSelecionado.modalidade_nome) && (
                    <div className="mt-3">
                      <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-2">Penalidade</h3>
                      <div className="max-w-[140px]">
                        <NumInput
                          label="Penalidade"
                          sub="-10 pts cada"
                          value={formPrecisao.qtd_penalidade}
                          onChange={v => setFormPrecisao(prev => ({ ...prev, qtd_penalidade: v }))}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-4 p-4 bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-xl flex items-center justify-between">
                    <span className="font-semibold text-compumaq-doradoClaro text-base">Total de Pontos</span>
                    <span className="text-4xl font-bold text-compumaq-doradoClaro">{calcTotalPontosFront(formPrecisao)}</span>
                  </div>
                </div>
              )}

              {/* Formulário Silhueta */}
              {itemSelecionado.modalidade_grupo === 'Silhueta' && (
                <div>
                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Acertos por figura</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {(['galinha', 'porco', 'peru', 'carneiro'] as const).map(f => (
                      <NumInput
                        key={f}
                        label={f.charAt(0).toUpperCase() + f.slice(1)}
                        value={formSilhueta[f]}
                        onChange={v => setFormSilhueta(prev => ({ ...prev, [f]: v }))}
                      />
                    ))}
                  </div>
                  <div className="mt-4 p-4 bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-xl flex items-center justify-between">
                    <span className="font-semibold text-compumaq-doradoClaro text-base">Total de Acertos</span>
                    <span className="text-4xl font-bold text-compumaq-doradoClaro">{calcTotalSilhuetaFront(formSilhueta)}</span>
                  </div>
                </div>
              )}

              {/* Formulário IPSC */}
              {itemSelecionado.modalidade_grupo === 'IPSC' && (
                <div>
                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">
                    Zonas de acerto ({modalidadeSelecionada?.power_factor === 'major' ? 'Major' : 'Minor'})
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    <NumInput
                      label="A" sub={modalidadeSelecionada?.power_factor === 'major' ? '5 pts' : '5 pts'}
                      value={formIpsc.qtd_a}
                      onChange={v => setFormIpsc(prev => ({ ...prev, qtd_a: v }))}
                      highlight
                    />
                    <NumInput
                      label="C" sub={modalidadeSelecionada?.power_factor === 'major' ? '4 pts' : '3 pts'}
                      value={formIpsc.qtd_c}
                      onChange={v => setFormIpsc(prev => ({ ...prev, qtd_c: v }))}
                    />
                    <NumInput
                      label="D" sub={modalidadeSelecionada?.power_factor === 'major' ? '2 pts' : '1 pt'}
                      value={formIpsc.qtd_d}
                      onChange={v => setFormIpsc(prev => ({ ...prev, qtd_d: v }))}
                    />
                    <NumInput
                      label="Miss" sub="0 pts"
                      value={formIpsc.qtd_miss}
                      onChange={v => setFormIpsc(prev => ({ ...prev, qtd_miss: v }))}
                    />
                  </div>

                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-2 mt-4">Penalidade e tempo</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput
                      label="Penalidade" sub="-10 pts cada"
                      value={formIpsc.qtd_penalidade}
                      onChange={v => setFormIpsc(prev => ({ ...prev, qtd_penalidade: v }))}
                    />
                    <div className="flex flex-col items-center rounded-xl border border-compumaq-dourado/25 bg-compumaq-pretoSuave p-2">
                      <span className="text-xs font-bold text-compumaq-textoSuave mb-1">Tempo (segundos)</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min={0}
                        value={formIpsc.tempo_segundos}
                        onChange={e => setFormIpsc(prev => ({ ...prev, tempo_segundos: e.target.value }))}
                        className="w-full text-center text-lg font-bold border rounded-lg py-1.5 border-compumaq-dourado/25 text-compumaq-texto focus:outline-none focus:ring-2 focus:ring-compumaq-dourado"
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-xl space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-compumaq-doradoClaro text-sm">Pontos</span>
                      <span className="text-2xl font-bold text-compumaq-doradoClaro">
                        {calcPontosIpscFront(formIpsc, modalidadeSelecionada?.power_factor)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-compumaq-doradoClaro text-sm">Hit Factor</span>
                      <span className="text-2xl font-bold text-compumaq-doradoClaro">
                        {calcHitFactorFront(calcPontosIpscFront(formIpsc, modalidadeSelecionada?.power_factor), formIpsc).toFixed(4)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Botoes de acao */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-3 border-t border-compumaq-dourado/25">
                <button
                  type="button"
                  onClick={() => setFormularioOpen(false)}
                  className="w-full sm:w-auto px-4 py-3.5 sm:py-2.5 border border-compumaq-dourado/25 text-sm rounded-xl hover:bg-compumaq-preto font-medium touch-manipulation"
                >
                  Cancelar
                </button>
                <>
                    <button
                      type="button"
                      onClick={handleSalvarEImprimir}
                      disabled={isPending}
                      title={btConnected ? 'Impressão direta Bluetooth (ESC/POS)' : 'Salvar e imprimir (abre diálogo se Bluetooth não conectado)'}
                      className={`w-full sm:w-auto px-4 py-3.5 sm:py-2.5 text-white text-base sm:text-sm rounded-xl disabled:opacity-50 font-bold touch-manipulation flex items-center justify-center gap-1.5 ${
                        btConnected
                          ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                          : 'bg-compumaq-preto hover:bg-compumaq-preto active:bg-compumaq-preto'
                      }`}
                    >
                      {btConnected ? (
                        /* ícone Bluetooth quando conectado */
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
                        </svg>
                      )}
                      {isPending ? '...' : btConnected ? 'Salvar e Imprimir (BT)' : 'Salvar e Imprimir'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSalvarEImprimirThermal}
                      disabled={isPending}
                      title="Salvar e imprimir termica simples (window.print)"
                      className="w-full sm:w-auto px-4 py-3.5 sm:py-2.5 bg-compumaq-pretoSuave border border-compumaq-dourado/25 text-compumaq-texto text-base sm:text-sm rounded-xl hover:bg-compumaq-preto active:bg-compumaq-preto disabled:opacity-50 font-bold touch-manipulation flex items-center justify-center gap-1.5"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
                      </svg>
                      {isPending ? '...' : 'Termica'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSalvar}
                      disabled={isPending}
                      className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 bg-compumaq-dourado text-compumaq-texto text-base sm:text-sm rounded-xl hover:bg-compumaq-doradoClaro active:bg-compumaq-dourado disabled:opacity-50 font-bold touch-manipulation"
                    >
                      {isPending ? 'Salvando...' : (itemSelecionado.tem_resultado ? 'Atualizar Resultado' : 'Lancar Resultado')}
                    </button>
                </>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
